import { useEffect, useState } from "react";
import axios from "axios";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

export default function AdminHeaderEditor() {
    const [slides, setSlides] = useState([]);
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openIndex, setOpenIndex] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState({});

    useEffect(() => {
        const fetchSlides = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/public/slides", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setSlides(res.data.content || []);
            } catch (error) {
                console.error("Error fetching slides:", error);
                toast.error("Error fetching items");
            } finally {
                setLoading(false);
            }
        };
        
        fetchSlides();
    }, []);

    useEffect(() => {
        const fetchPages = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/content", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setPages(res.data);
            } catch (error) {
                console.error("Error fetching pages:", error);
            }
        };
        
        fetchPages();
    }, []);

    const handleChange = (index, field, value) => {
        const updated = [...slides];
        updated[index][field] = value;
        setSlides(updated);
    };

    const moveSlide = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= slides.length) return;

        const updated = [...slides];
        const temp = updated[index];
        updated[index] = updated[newIndex];
        updated[newIndex] = temp;

        setSlides(updated);
        setOpenIndex(newIndex);
    };

    const saveSlides = async () => {
        setSaving(true);
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/slides", { content: slides }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success("Slides saved!");
        } catch {
            toast.error("Failed to save slides");
        } finally {
            setSaving(false);
        }
    };

    const addSlide = () => {
        const newSlide = {
            name: "",
            description: "",
            image: "",
            link: "",
            link_label: ""
        };
        setSlides([newSlide, ...slides]);
        setOpenIndex(0);
    };

    const deleteSlide = async (index) => {
        const slide = slides[index];
        const ok = await confirmSwal({
            title: `Delete slide ${slide.name || "(No Name)"}?`,
            text: "Once deleted it cannot be restored.",
            icon: "warning",
            confirmText: "Delete",
            confirmColor: "#d33",
        });
        if (!ok) return;

        const updated = slides.filter((_, i) => i !== index);
        setSlides(updated);
        setOpenIndex(null);

        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/slides", { content: updated }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.info("Slide deleted");
        } catch {
            toast.error("Failed to save after deletion");
        }
    };

    const toggleOpen = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const handleImageUpload = async (index, file) => {
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPG, PNG, and HEIC files are allowed');
            return;
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        setUploading({ ...uploading, [index]: true });

        try {
            const token = await auth.currentUser.getIdToken();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('subfolder', 'slides');

            const response = await axios.post('/api/upload-image', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                handleChange(index, 'image', response.data.url);
                toast.success('Image uploaded successfully!');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
            setUploading({ ...uploading, [index]: false });
        }
    };

    if (loading) return <p>Loading slides...</p>;

    return (
        <div>
            <h4>Header Slideshow</h4>
            <p className="text-body-secondary">Slides are for the changing header slideshow on the homepage.</p>

            <button className="btn btn-admin mb-3" onClick={addSlide}>+ Add Slide</button>

            <div className="accordion" id="projectsAccordion">
                {slides.map((slide, index) => (
                    <div className="accordion-item" key={index}>
                        <h2 className="accordion-header" id={`heading-${index}`}>
                            <button
                                className={`accordion-button ${openIndex === index ? "" : "collapsed"}`}
                                type="button"
                                onClick={() => toggleOpen(index)}
                            >
                                <strong>{slide.name || "(No Name)"}</strong>
                            </button>
                        </h2>
                        <div className={`accordion-collapse collapse ${openIndex === index ? "show" : ""}`}>
                            <div className="accordion-body">
                                <div className="mb-2">
                                    <label className="form-label">Name</label>
                                    <input className="form-control" value={slide.name} onChange={(e) => handleChange(index, "name", e.target.value)} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Description</label>
                                    <textarea className="form-control" value={slide.description} onChange={(e) => handleChange(index, "description", e.target.value)} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Image</label>
                                    <input 
                                        className="form-control mb-2" 
                                        value={slide.image} 
                                        onChange={(e) => handleChange(index, "image", e.target.value)}
                                        placeholder="Image URL"
                                    />
                                    <div className="input-group">
                                        <input 
                                            type="file" 
                                            className="form-control" 
                                            accept=".jpg,.jpeg,.png,.heic"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) handleImageUpload(index, file);
                                            }}
                                            disabled={uploading[index]}
                                        />
                                        <button 
                                            className="btn btn-outline-secondary" 
                                            type="button"
                                            disabled
                                        >
                                            {uploading[index] ? 'Uploading...' : 'Upload'}
                                        </button>
                                    </div>
                                    {slide.image && (
                                        <div className="mt-2">
                                            <img 
                                                src={slide.image} 
                                                alt="Preview" 
                                                style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
                                                className="img-thumbnail"
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Learn More Link</label>
                                    <select
                                        className="form-select"
                                        value={slide.link}
                                        onChange={(e) => handleChange(index, "link", e.target.value)}
                                    >
                                        <option value="">(No link)</option>
                                        {pages.map((p) => (
                                            <option key={p.slug} value={p.slug}>
                                                {p.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Learn More Label <small className="text-body-secondary">(Optional)</small></label>
                                    <input className="form-control" placeholder="Learn More" value={slide.link_label || ""} onChange={(e) => handleChange(index, "link_label", e.target.value)} />
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <button
                                            className="btn btn-sm btn-outline-secondary me-2"
                                            onClick={() => moveSlide(index, -1)}
                                            disabled={index === 0}
                                        >
                                            ↑ Move Up
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => moveSlide(index, 1)}
                                            disabled={index === slides.length - 1}
                                        >
                                            ↓ Move Down
                                        </button>
                                    </div>
                                    <button className="btn btn-outline-danger btn-sm" onClick={() => deleteSlide(index)} disabled={saving}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <button className="btn btn-admin mt-3" onClick={saveSlides} disabled={saving}>
                {saving ? "Saving..." : "Save"}
            </button>
        </div>
    );
}
